import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NewsletterSignup = ({ variant = 'default' }) => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      toast.error('Bitte gib eine gültige E-Mail-Adresse ein.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API_URL}/api/newsletter/subscribe`, {
        email: email.trim()
      });

      if (response.data.success) {
        setIsSuccess(true);
        toast.success(response.data.message);
        setEmail('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Ein Fehler ist aufgetreten. Bitte versuche es später erneut.';
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Success state
  if (isSuccess) {
    return (
      <div className={`flex items-center gap-3 ${variant === 'footer' ? 'text-gray-300' : 'text-gray-700'}`}>
        <CheckCircle className="w-5 h-5 text-[#009E9A] flex-shrink-0" />
        <span className="text-[15px]">
          Prüfe deinen Posteingang und bestätige die Anmeldung!
        </span>
      </div>
    );
  }

  // Footer variant - compact horizontal layout
  if (variant === 'footer') {
    return (
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="email"
              placeholder="Deine E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-12 h-11 text-[15px] bg-gray-800 border-gray-700 text-white placeholder:text-gray-500 focus:border-[#009E9A] focus:ring-[#009E9A] rounded-lg"
              disabled={isLoading}
              data-testid="newsletter-email-footer"
            />
          </div>
          <Button 
            type="submit" 
            disabled={isLoading}
            className="bg-[#009E9A] hover:bg-[#00857e] text-white px-5 h-11 text-[15px] rounded-lg"
            data-testid="newsletter-submit-footer"
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              'Anmelden'
            )}
          </Button>
        </div>
        <p className="text-[13px] text-gray-500">
          Mit der Anmeldung akzeptierst du unsere Datenschutzbestimmungen.
        </p>
      </form>
    );
  }

  // Default/Homepage variant - more prominent
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-8 md:p-10">
      <div className="max-w-xl mx-auto text-center">
        <h3 className="text-xl md:text-2xl font-bold text-[#0B1930] mb-3">
          Bleib auf dem Laufenden
        </h3>
        <p className="text-gray-600 mb-8 text-[15px] md:text-base">
          Erhalte aktuelle GmbH-Angebote, Tipps zur Gründung und exklusive Insights – direkt in dein Postfach.
        </p>
        
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
          <div className="relative flex-1">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="email"
              placeholder="Deine E-Mail-Adresse"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-12 h-12 text-[15px] border-gray-200 focus:border-[#009E9A] focus:ring-[#009E9A] rounded-lg"
              disabled={isLoading}
              data-testid="newsletter-email-home"
            />
          </div>
          <Button 
            type="submit" 
            disabled={isLoading}
            className="h-12 px-7 bg-[#0B1930] hover:bg-[#162844] text-white font-semibold text-[15px] rounded-lg"
            data-testid="newsletter-submit-home"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                Wird gesendet...
              </>
            ) : (
              'Anmelden'
            )}
          </Button>
        </form>
        
        <p className="text-[13px] text-gray-500 mt-5">
          Kein Spam. Du kannst dich jederzeit abmelden. <a href="/rechtliches#datenschutz" className="underline hover:text-[#009E9A]">Datenschutz</a>
        </p>
      </div>
    </div>
  );
};

export default NewsletterSignup;
